import { prepUrlForEnvironment } from '@sureapp/wl-toggle-common/dist/utils/url';
import { Link } from 'react-router-dom';

import ToyotaFooterHeader from '@@/auto/components/Footer/headers/ToyotaFooterHeader';
import KnockoutCopy from '@@/auto/components/KnockoutCopy/KnockoutCopy';
import Text from '@@/auto/components/LayoutContent/Text';
import ToyotaHero from '@@/auto/components/LayoutSignUp/heroes/ToyotaHero';
import { TNavLinkWLConfigOptions } from '@@/auto/components/NavBar/useNavLinks';
import {
  TConfig,
  TConfigInput,
  TConfigParams,
  TGetAddOns,
} from '@@/auto/interfaces';
import { excludeFalse } from '@@/auto/utils/excludeFalse';
import { ReactComponent as RentersPolicyIcon } from '@@/common/assets/toyota/image/building.svg';
import builtOnSureLogo from '@@/common/assets/toyota/image/built-on-sure.svg';
import { ReactComponent as CreditCardIcon } from '@@/common/assets/toyota/image/card.svg';
import { ReactComponent as ToyotaCaretIcon } from '@@/common/assets/toyota/image/caret.svg';
import { ReactComponent as TenantVehicleIcon } from '@@/common/assets/toyota/image/corolla.svg';
import idCardsIconSrc from '@@/common/assets/toyota/image/document-multiple.png';
import policyIconSrc from '@@/common/assets/toyota/image/document-single.png';
import errorImg from '@@/common/assets/toyota/image/errorImg.svg';
import { ReactComponent as IdIcon } from '@@/common/assets/toyota/image/id_card_icon.svg';
import { ReactComponent as TenantInfoIconSolid } from '@@/common/assets/toyota/image/info-icon.svg';
import peopleLaughingImgPng from '@@/common/assets/toyota/image/people-laughing.png';
import peopleLaughingImgWebP from '@@/common/assets/toyota/image/people-laughing.webp';
import { ReactComponent as TenantAlertIcon } from '@@/common/assets/toyota/image/red-alert.svg';
import selfServiceHero from '@@/common/assets/toyota/image/self-service-hero.min.svg';
import { ReactComponent as RoadsideAssistanceIcon } from '@@/common/assets/toyota/image/tow_truck_icon.svg';
import toyotaProtectAssetsImg from '@@/common/assets/toyota/image/toyota-assets.svg';
import tenantDesktopBuckleUpImg from '@@/common/assets/toyota/image/toyota-buckle-up-desktop.svg';
import tenantMobileBuckleUpImg from '@@/common/assets/toyota/image/toyota-buckle-up-mobile.svg';
import { ReactComponent as TenantCarIcon } from '@@/common/assets/toyota/image/toyota-car-icon.svg';
import { ReactComponent as SuccessIcon } from '@@/common/assets/toyota/image/toyota-checkmark.svg';
import { ReactComponent as TenantDriverIcon } from '@@/common/assets/toyota/image/toyota-driver-icon.v2.svg';
import tenantCityImg from '@@/common/assets/toyota/image/toyota-little-city.svg';
import { ReactComponent as ToyotaLogo } from '@@/common/assets/toyota/image/toyota-logo.svg';
import mfaBaseImage from '@@/common/assets/toyota/image/toyota-mfa-base.svg';
import mfaImage from '@@/common/assets/toyota/image/toyota-mfa.svg';
import toyotaProtectLovedImg from '@@/common/assets/toyota/image/toyota-protect-you-and-loved-ones.svg';
import toyotaProtectVehicleImg from '@@/common/assets/toyota/image/toyota-vehicle.svg';

import { featureFlags } from '../featureFlags';

const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
  shouldUseCurrentHost: (hostname: string) => hostname === 'localhost',
});

const toyotaAutoInsuranceUrl = 'https://toyotaims.com/toyotaautoinsurance';

function getConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return {
    accidentsAndViolationsViewConfig: {
      cancelButtonLabel: 'Back Up',
      nextButtonLabel: 'Drive On',
    },
    auto: {
      addOns: {
        options: {
          towing_road_services: {
            copy: {
              modalContent: [
                'Towing & Road Service(s) is an optional coverage and pays for the cost of towing and other roadside services like a locksmith, flat tire changing, and battery jumpstart in the event your vehicle is disabled (subject to the Service Call conditions).',
                'Are you the owner of a new Toyota? Every new Toyota comes with ToyotaCare, a two-year maintenance and roadside assistance program that covers towing, battery jump-starts, lockout protection, and more. If your ToyotaCare has expired, you should consider purchasing this additional coverage.',
              ],
            },
          },
          oem_parts: {
            copy: {
              modalContent:
                'This is an optional coverage that pays for Toyota Genuine Parts in the event that your vehicle is damaged in a covered loss.',
              description:
                "If you're passionate about your car (i.e., it has a name) and want only factory part replacements in the event of an accident, then this is the coverage for you (and Princess Priscilla von Prius).",
            },
          },
        },
        getAddOns: ({ userAction, userFlow }: Parameters<TGetAddOns>[0]) =>
          excludeFalse([
            'towing_road_services',
            'temporary_ride',
            'side_hustle',
            userFlow === 'self-service' && 'gap',
            'oem_parts',
            'new_car_replacement',
            userAction === 'editing-addons' && 'pet_passenger',
          ] as const),
      },
    },
    addonModalConfig: {
      carbonOffsetDescriptionCopy: '',
    },
    brazeConfig: {
      isBrazeEnabled: false,
    },
    enabledSideEffects: isFeatureEnabled('isToggleTXExpansionEnabled')
      ? ['ENABLE_OEM_ON_COMP_COLL']
      : [],
    errorBoundaryConfig: {
      email: 'support@tai.com',
    },
    fetchingViewConfig: {
      defaultAdpfHeader: isFeatureEnabled(featureFlags.CA_EXPANSION_CODE)
        ? 'Buckle up.'
        : 'Hang here for just a minute.',
      defaultSubheader: "We're personalizing your quote...",
      tenantDesktopImg: tenantDesktopBuckleUpImg,
      tenantMobileImg: tenantMobileBuckleUpImg,
    },
    collectEmailViewConfig: {
      isEnabled: false,
    },
    cmsSlugs: {
      authenticatedFooterSlug: 'toyota-footer',
      copyrightSlug: 'toyota-copyright',
      footerSlug: 'toyota-footer',
      privacyPolicySlug: 'toyota-privacy-policy',
      termsViewSlug: 'toyota-terms-and-conditions',
    },
    coverage: {
      compColl: {
        modalContent:
          'Comprehensive pays for accidental damage to your car from things other than a collision - like floods, hail, fire, theft, and windshield or animal damage minus your chosen deductible. Collision pays for accidental damage to your car caused by collision with another vehicle or fixed object minus your chosen deductible. Deductibles chosen are per vehicle.',
      },
      uninsuredMotoristPropertyDamage: {
        description: `Your bumper sticker might say 'don't hit me if you're uninsured' but it isn't actually protecting you against that. Don't worry, this coverage can help.`,
      },
    },
    coverageConfig: (coverageConfigProps: TConfig['coverageConfig']) => {
      return {
        ...coverageConfigProps,
        subHeader: `To start, we've made selections based on your current policy. Adjust items to get the exact coverage you want. Or, if everything looks good, just skip to the next step.`,
        getOverviewLineTextVariant: (isAddOn: boolean) =>
          isAddOn ? 'body2' : 'h5',
        tenantAssetsImg: toyotaProtectAssetsImg,
        tenantLoveImg: toyotaProtectLovedImg,
        tenantVehicleImg: toyotaProtectVehicleImg,
      };
    },
    coverageStartDateConfig: {
      infoCopy: null,
      labelCopy: 'Select a start date',
      nextButtonCopy: 'Drive on',
      cancelButtonCopy: 'Back up',
    },
    cssFiles: ['toyota.css'],
    datePickerConfig: {
      placeholder: 'mm/dd/yyyy',
    },
    discountsConfig: {
      hasCarbonOffsetDiscount: false,
      hasPartnerDiscount: true,
      partnerDiscountDescription:
        'You are eligible for this discount because Toyota Insurance Management Solutions is an affinity partner of Toggle, a member of the Farmers Insurance family and the underwriter of Toyota Auto Insurance.',
    },
    editPolicyReviewConfig: { editButtonVariant: 'ACTION' },
    errorPageConfig: {
      backgroundImgSrc: errorImg,
    },
    excludeDriversViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    excludeVehicleViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    featureFlags: {
      isAccountManagementEnabled: isFeatureEnabled(
        'isToyotaAccountManagementEnabled'
      ),
      isAutoPurchaseFlowEnabled: isFeatureEnabled(
        'isToyotaAutoPurchaseFlowEnabled'
      ),
      isAutoQuoteLinkExchangeEnabled: isFeatureEnabled(
        'isToyotaAutoQuoteLinkExchangeEnabled'
      ),
      isDirectToAutoQuoteEnabled: isFeatureEnabled(
        'isToyotaDirectToAutoQuoteEnabled'
      ),
      isLogoRedirectInAutoPurchaseFlowEnabled: isFeatureEnabled(
        'isToyotaLogoRedirectInAutoPurchaseFlowEnabled'
      ),
      isPrivacyAndTermsEnabled: isFeatureEnabled(
        'isToyotaPrivacyAndTermsEnabled'
      ),
      isQuoteAssistForTenantEnabled:
        isFeatureEnabled('isQuoteAssistEnabled') &&
        isFeatureEnabled('isToyotaQuoteAssistEnabled'),
      isRentersQuoteCreationEnabled: isFeatureEnabled(
        'isToyotaRentersQuoteCreationEnabled'
      ),
      isSelfServiceEnabled: isFeatureEnabled('isToyotaSelfServiceEnabled'),
    },
    footerConfig: {
      hasNavItems: false,
      hasSocialLinks: false,
      header: <ToyotaFooterHeader logo={<ToyotaLogo />} />,
      sureFooterLogoSrc: builtOnSureLogo,
      tenantPhoneVariant: 'second-row',
    },
    formDisclaimer: (
      <>
        By clicking &quot;PRICE IT FOR ME,&quot; you agree that we may use
        information from you and 3rd party sources (like your credit, claims
        and/or driving histories) to prepare your personalized quote. Don’t
        worry, getting your price will not affect your credit score. To learn
        more about our information practices, including how to access and
        correct your information, please review our{' '}
        <Link key="privacy-policy-link" to="/privacy">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link key="noip-link" to="/privacy">
          Notice of Information Practices
        </Link>
        .
      </>
    ),
    icons: {
      CaretIcon: ToyotaCaretIcon,
      SuccessIcon,
      TenantAlertIcon,
      AlertIcon: TenantAlertIcon,
      TenantCarIcon,
      TenantDriverIcon,
      TenantInfoIconSolid,
      TenantVehicleIcon,
      CreditCardIcon,
      NewCreditCardIcon: CreditCardIcon,
      autoPolicyIcons: {
        AutoPolicyIcon: TenantVehicleIcon,
        IdIcon,
        RoadsideAssistanceIcon,
        idCardsIconSrc,
        policyIconSrc,
      },
      rentersPolicyIcons: {
        RentersPolicyIcon,
      },
      trustIcons: [
        'pf-visa',
        'pf-mastercard-alt',
        'pf-american-express-alt',
        'pf-discover',
      ],
    },
    iidKnockoutCopy: {
      featureFlag: 'isUpdatedToyotaKnockoutCopy',
      subHeader: <KnockoutCopy phoneNumber="888-492-1780" />,
    },
    ineligibleAddressConfig: {
      getEligibilityText: ({
        isNewRegion,
        newState,
        oldState,
      }: Parameters<
        NonNullable<TConfig['ineligibleAddressConfig']['getEligibilityText']>
      >[0]) =>
        isNewRegion
          ? `Nice! Toyota Auto Insurance is available in ${newState}. We can cancel your ${oldState} policy and start your new ${newState} policy today.`
          : `Sorry! Toyota Auto Insurance isn't available in ${newState}. But we'll help you cancel your policy if you'd like.`,
      getCancellationCopy: ({
        oldState,
        date,
      }: {
        oldState?: string;
        date?: string;
      }) => (
        <>
          Okay, your {oldState} cancellation date will be today,{' '}
          <strong>{date}</strong>.
        </>
      ),
    },
    layoutContentConfig: {
      tenantCityImg,
    },
    layoutQuoteConfig: {
      isLogoHeader: false,
      summaryPosition: 'sticky',
      variant: '100vw-footer',
      summaryCopy: 'Your current auto insurance quote:',
    },
    layoutSelfServiceConfig: {
      backgroundImgSrc: selfServiceHero,
    },
    localforageDbName: 'toyota',
    loginConfig: {
      backgroundImgSrc: peopleLaughingImgPng,
    },
    logos: {
      default: ToyotaLogo,
      light: ToyotaLogo,
      title: 'Toyota | Toggle',
    },
    mfaConfig: {
      mfaImage,
      mfaBaseImage,
    },
    missingLicenseViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    missingLicenseInfoViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    missingLienholderViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    missingVinViewConfig: {
      submitButtonLabel: 'Drive On',
    },
    modalAdjustedPriceConfig: {
      footerPrimaryButtonCopy: 'Got it. Drive on.',
    },
    navBarConfig: {
      getLinks: ({ isLoggedIn }: TNavLinkWLConfigOptions) => {
        const loggedInMenuLinks = [
          {
            to: '/my-account',
            title: 'My policies',
            dataTestId: 'policies',
          },
          {
            to: '/my-activity',
            title: 'My payments',
            dataTestId: 'payments',
          },
          {
            to: '/my-profile',
            title: 'My profile',
            dataTestId: 'profile',
          },
          {
            to: process.env.REACT_APP_CLAIMS_URL!,
            title: 'Claims',
            dataTestId: 'claims',
          },
          {
            to: process.env.REACT_APP_FAQ_URL!,
            title: 'FAQs',
            dataTestId: 'faqs',
          },
        ];

        const loggedInFooterLinks = [
          { to: process.env.REACT_APP_ABOUT_US_URL!, title: 'About Us' },
          { to: '/terms', title: 'Terms' },
          { to: '/privacy', title: 'Privacy' },
        ];

        return {
          main: isLoggedIn ? loggedInMenuLinks : [],
          footer: isLoggedIn ? loggedInFooterLinks : [],
          shouldHidePhoneLink: true,
          shouldHideSocialLinks: true,
          loginLogout: isLoggedIn ? { to: '/logout', title: 'Log out' } : null,
        };
      },
    },
    noAutoCoverageConfig: {
      ctaSubmitCopy: `Thanks! We’ll be in touch as soon as Toyota Auto Insurance is available in your state.`,
      getNoCoverageCopy: ({ regionName }: { regionName: string }) => (
        <>
          {`Toyota Auto Insurance is not in ${regionName} yet, but we can still help
        you, call `}
          <a href="tel:8772490086">877-249-0086</a>.
        </>
      ),
      secondaryCopy: 'Let me know when Toyota Auto comes my way!',
    },
    notFoundPageConfig: {
      backgroundImgSrc: '',
      ctaHref: 'https://toyotaautoinsurance.com',
    },
    notifyConfig: {
      discountCopy:
        'Congratulations! Being a Toyota customer unlocks a discount and exclusive features.',
    },
    partnerCode: 'toggle_toyota',
    passwordValidator: {
      passingIconType: 'circle-fill',
    },
    primaryVehicleDataSource: 'toyota',
    privacyPolicyView: {
      insuranceUrl: 'app.toyotaautoinsurance.com',
    },
    purchaseFlowConfig: {
      checkoutExistingToggleCustomerAlreadyLoggedInCopy:
        "Looks like you're already a Toggle renters customer. Nice!",
      checkoutExistingToggleCustomerLoginSuccessCopy:
        'Great! As an existing Toggle customer, you may be eligible for an additional discount.',
      checkoutExistingToggleCustomerPrompt: (
        <>
          <Text key="existing-prompt-text-1" variant="inherit">
            Do you have Toggle® renters insurance?
          </Text>
          <Text key="existing-prompt-text-2" variant="inherit">
            (Toyota Auto Insurance is powered by Toggle, a member of the
            Farmers® Insurance family.)
          </Text>
        </>
      ),
      isCheckoutToggleCustomerDefault: false,
    },
    quoteExpirationCondition: {
      maxElapsedTimeMinutes: 60,
      onConfirmDestination: toyotaAutoInsuranceUrl,
      timestampKey: 'updated_at',
    },
    redirectUrls: {
      indexRedirectUrl: toyotaAutoInsuranceUrl,
      mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
      isPartnerRedirectUrlEnabled: true,
    },
    reviewAutoQuoteConfig: {
      nextButtonCopy: 'Next Stop: Signatures',
    },
    signUpViewConfig: {
      backgroundPicture: {
        alt: 'Young adults laughing',
        sources: [
          {
            src: peopleLaughingImgWebP,
            type: 'image/webp',
          },
          {
            src: peopleLaughingImgPng,
            type: 'image/png',
          },
        ],
        fallbackSrc: peopleLaughingImgPng,
      },
      heroVariant: 'above-form',
      Hero: ToyotaHero,
      title: 'Cover Your Stuff | Toyota Insurance',
      copy: {
        header:
          "Customize your policy and never pay for coverage you don't want.",
        hero1: 'Toyota Auto Insurance',
        hero2: 'Protecting your journey, wherever the road takes you.',
        informationalHeader: 'But first...',
        informationalSubHeader: 'Confirm the details below.',
      },
      hasMainNav: false,
    },
    stripeElStyles: {
      base: {
        fontFamily: "'Toyota Type', Helvetica, sans-serif",
        fontSize: '14px',
        color: '#696969',
        '::placeholder': {
          color: '#787878',
        },
      },
      fonts: [
        {
          family: 'Toyota Type',
          // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
          src: `url(https://sure-host.s3.amazonaws.com/static/toyota/ToyotaType-Regular.ttf) format('truetype')`,
        },
      ],
      invalid: {
        iconColor: '#e50c22',
        color: '#696969',
      },
    },
    supportEmail: 'support@tai.team',
    tenantDisplayName: 'Toyota',
    tenantPhoneNumber: '1-833-210-7029',
    vehicleManagementCopyConfig: {
      subheader:
        "We've already included your Toyota. Feel free to add additional vehicles.",
    },
  };
}

export default getConfig;
